import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material'
import {
  BarChart3,
  Star,
  MessageCircle,
  Users,
  TrendingUp,
  Calendar,
  Flame,
  Zap,
  Mic,
} from 'lucide-react'
import {
  useGetEngagementStatsQuery,
  useGetAudienceInsightsQuery,
  useGetEpisodePerformanceQuery,
} from 'graphql/generated/graphql'
import { LOCAL, DEVELOP } from 'constants/env'
import InfoTooltip from 'components/InfoTooltip/InfoTooltip'

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

const platformEmoji: Record<string, string> = {
  Instagram: '📷', TikTok: '🎵', Youtube: '🎥', Reddit: '🔴', Facebook: '📘', Twitter: '🐦', Bluesky: '🦋',
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function Analytics(): JSX.Element {
  const { data: engagementData } = useGetEngagementStatsQuery({ pollInterval: 60000 })
  const { data: insightsData } = useGetAudienceInsightsQuery()
  const { data: episodeData } = useGetEpisodePerformanceQuery()
  const [podcastStats, setPodcastStats] = useState<any>(null)
  const [loadingStats, setLoadingStats] = useState(true)
  const [statsError, setStatsError] = useState<string | null>(null)

  const stats = engagementData?.getEngagementStats
  const insights = insightsData?.getAudienceInsights
  const episodes = episodeData?.getEpisodePerformance ?? []
  const episodeSignals = episodes.filter(
    (ep) => ep.topicName.startsWith('EP:') || ep.topicName.startsWith('Latest:')
  )

  useEffect(() => {
    fetch(`${config.API_URL}/api/podcast/stats`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json()
        if (data.success) {
          setPodcastStats(data.data)
        } else {
          setStatsError('Could not load podcast stats.')
        }
      })
      .catch(() => setStatsError('Could not connect to load podcast stats.'))
      .finally(() => setLoadingStats(false))
  }, [])

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 3, fontSize: { xs: '24px', md: '34px' } }}>
        Analytics
      </Typography>

      {/* Engagement Summary */}
      {stats && stats.total > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
          <StatCard icon={<Zap size={16} />} label="Today" value={stats.today} />
          <StatCard icon={<Calendar size={16} />} label="This Week" value={stats.thisWeek} />
          <StatCard icon={<TrendingUp size={16} />} label="This Month" value={stats.thisMonth} />
          <StatCard icon={<MessageCircle size={16} />} label="Total" value={stats.total} />
          {stats.streakDays > 1 && (
            <StatCard icon={<Flame size={16} />} label="Day Streak" value={stats.streakDays} highlight />
          )}
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        {/* Podcast Ratings from Podchaser */}
        {podcastStats?.podcast && (
          <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Mic size={16} color="#a855f7" />
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Podcast Ratings<InfoTooltip text="Your podcast's rating and review data from Podchaser. Updated daily." />
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {podcastStats.podcast.imageUrl && (
                <Box component="img" src={podcastStats.podcast.imageUrl} sx={{ width: 56, height: 56, borderRadius: '12px', objectFit: 'cover' }} />
              )}
              <Box>
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '16px' }}>{podcastStats.podcast.title}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {podcastStats.podcast.ratingAverage != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Star size={14} color="#f59e0b" fill="#f59e0b" />
                      <Typography sx={{ color: '#f59e0b', fontWeight: 700, fontSize: '14px' }}>
                        {String(podcastStats.podcast.ratingAverage.toFixed(1))}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
                        ({String(podcastStats.podcast.ratingCount)} ratings)
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                {String(podcastStats.podcast.numberOfEpisodes)} episodes
              </Typography>
              {podcastStats.podcast.avgEpisodeLength && (
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                  ~{String(podcastStats.podcast.avgEpisodeLength)} min avg
                </Typography>
              )}
              {podcastStats.podcast.daysBetweenEpisodes && (
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                  Every {String(podcastStats.podcast.daysBetweenEpisodes)} days
                </Typography>
              )}
              {podcastStats.podcast.reviewCount > 0 && (
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                  {String(podcastStats.podcast.reviewCount)} reviews
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Platform Breakdown */}
        {insights && insights.platformBreakdown.length > 0 && (
          <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>
              Where Your Audience Lives<InfoTooltip text="Which social platforms the creators in your niche are most active on, based on the content your signals have found." />
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {insights.platformBreakdown.map((p) => (
                <Box key={p.platform} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontSize: '14px', width: 24, textAlign: 'center' }}>{platformEmoji[p.platform] ?? '🌐'}</Typography>
                  <Typography sx={{ color: 'white', fontSize: '13px', fontWeight: 500, width: 70 }}>{p.platform}</Typography>
                  <Box sx={{ flex: 1, height: 8, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                    <Box sx={{ width: `${String(p.percentage)}%`, height: '100%', background: 'linear-gradient(135deg, #a855f7, #6366f1)', borderRadius: 4 }} />
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', width: 35, textAlign: 'right' }}>{String(p.percentage)}%</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Follower Tiers */}
        {insights && insights.followerTiers.length > 0 && (
          <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>
              Creator Size Distribution<InfoTooltip text="Follower count breakdown of the creators your signals find. Nano (1K-10K) and Micro (10K-50K) creators are the most likely to engage back." />
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {insights.followerTiers.map((tier) => (
                <Box key={tier.tier} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 500, width: 110 }}>{tier.tier}</Typography>
                  <Box sx={{ flex: 1, height: 8, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                    <Box sx={{ width: `${String(tier.percentage)}%`, height: '100%', background: 'linear-gradient(135deg, #22c55e, #16a34a)', borderRadius: 4 }} />
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', width: 55, textAlign: 'right' }}>{String(tier.count)} ({String(tier.percentage)}%)</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Top Topics */}
        {insights && insights.topTopics.length > 0 && (
          <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>
              What Your Audience Talks About<InfoTooltip text="The most common themes extracted from posts your signals find. These are the topics your potential audience cares about most." />
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {insights.topTopics.map((topic, i) => (
                <Chip key={topic} label={topic} size="small" sx={{
                  backgroundColor: i < 3 ? 'rgba(168,85,247,0.15)' : 'rgba(255,255,255,0.05)',
                  border: i < 3 ? '1px solid rgba(168,85,247,0.3)' : '1px solid rgba(255,255,255,0.08)',
                  color: i < 3 ? '#a855f7' : 'rgba(255,255,255,0.6)', fontSize: '12px',
                }} />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Episode Performance */}
      {episodeSignals.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
            Episode Performance<InfoTooltip text="How each episode signal is performing. 'Found' = conversations matching the topic. 'Engaged' = conversations you've joined." />
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
            {episodeSignals.map((ep) => (
              <Box key={ep.topicId} sx={{ minWidth: 200, p: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', flexShrink: 0 }}>
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '13px', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ep.topicName.replace(/^(EP|Latest): ?/, '')}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', mb: 1 }}>{formatDate(ep.createdAt)}</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingUp size={12} color="rgba(255,255,255,0.4)" />
                    <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{String(ep.contentCount)} found</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MessageCircle size={12} color="#a855f7" />
                    <Typography sx={{ fontSize: '12px', color: '#a855f7', fontWeight: 600 }}>{String(ep.engagementCount)} engaged</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Engagement Trend */}
      {insights && insights.engagementTrend.length > 0 && (
        <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TrendingUp size={16} color="#a855f7" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Engagement Trend (14 days)<InfoTooltip text="Your daily engagement activity. Consistent engagement is key — even 10 minutes per day builds real momentum over time." />
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 80 }}>
            {insights.engagementTrend.map((day) => {
              const maxCount = Math.max(...insights.engagementTrend.map((d) => d.count), 1)
              const height = (day.count / maxCount) * 100
              return (
                <Box key={day.date} sx={{
                  flex: 1, height: `${String(Math.max(height, 4))}%`,
                  background: day.count > 0 ? 'linear-gradient(180deg, #a855f7, #6366f1)' : 'rgba(255,255,255,0.05)',
                  borderRadius: '3px 3px 0 0', minWidth: 8,
                }} title={`${day.date}: ${String(day.count)} engagements`} />
              )
            })}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px' }}>{insights.engagementTrend[0]?.date.slice(5) ?? ''}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px' }}>{insights.engagementTrend[insights.engagementTrend.length - 1]?.date.slice(5) ?? ''}</Typography>
          </Box>
        </Box>
      )}

      {/* Similar Podcasts from Podchaser */}
      {podcastStats?.similarPodcasts?.length > 0 && (
        <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', mb: 3 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>
            Similar Podcasts<InfoTooltip text="Podcasts similar to yours from Podchaser. These are natural cross-promotion partners — engage with their audience to grow yours." />
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
            {podcastStats.similarPodcasts.map((pod: any) => (
              <Box key={pod.id} sx={{ minWidth: 160, p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', flexShrink: 0, textAlign: 'center' }}>
                {pod.imageUrl && <Box component="img" src={pod.imageUrl} sx={{ width: 48, height: 48, borderRadius: '10px', objectFit: 'cover', mx: 'auto', mb: 1 }} />}
                <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pod.title}</Typography>
                {pod.ratingAverage && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', mt: 0.5 }}>
                    <Star size={10} color="#f59e0b" fill="#f59e0b" />
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>{String(pod.ratingAverage.toFixed(1))}</Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Recent Reviews from Podchaser */}
      {podcastStats?.reviews?.length > 0 && (
        <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>
            Recent Reviews<InfoTooltip text="Listener reviews from Podchaser. Responding to reviews builds loyalty and shows new listeners you care." />
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {podcastStats.reviews.map((review: any) => (
              <Box key={review.id} sx={{ p: 1.5, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  {Array.from({ length: review.rating }).map((_, i) => (
                    <Star key={i} size={12} color="#f59e0b" fill="#f59e0b" />
                  ))}
                  {review.author?.name && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', ml: 1 }}>by {review.author.name}</Typography>
                  )}
                </Box>
                {review.body && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', lineHeight: 1.5 }}>{review.body}</Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Loading / error state for Podchaser */}
      {loadingStats && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ color: '#a855f7' }} />
        </Box>
      )}
      {statsError && !loadingStats && !podcastStats && (
        <Box sx={{ p: 2, backgroundColor: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: '12px', textAlign: 'center' }}>
          <Typography sx={{ color: 'rgba(239,68,68,0.7)', fontSize: '13px' }}>
            {statsError}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

function StatCard({ icon, label, value, highlight }: { icon: React.ReactNode; label: string; value: number; highlight?: boolean }): JSX.Element {
  return (
    <Box sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: highlight ? '#f59e0b' : '#a855f7' }}>{icon}</Box>
      <Typography sx={{ color: highlight ? '#f59e0b' : 'white', fontWeight: 800, fontSize: '24px', lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', mt: 0.25 }}>{label}</Typography>
    </Box>
  )
}
